// src/components/Reports/PrintKit.jsx
// ======================================================================
// طباعة موحّدة للتقارير: ورقة A4 + ترويسة الشركة + الفترة + تذييل
//
//   const company = useCompanyInfo();
//   const printRef = useRef(null);
//   const print = useReportPrint(printRef, 'ميزان المراجعة');
//   <Box sx={{ display: 'none' }}>
//     <PrintSheet ref={printRef} title="..." period="..." company={company}> ...جداول بـ P.th / P.td </PrintSheet>
//   </Box>
// ======================================================================
import React, { forwardRef, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import axios from '../../axios';
import ReportPrintHeader from '../ReportPrintHeader';

const C = { ink: '#111827', muted: '#6b7280', line: '#d1d5db', soft: '#f3f4f6' };

/** أنماط جاهزة لجداول الطباعة */
export const P = {
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 12 },
  th: {
    border: `1px solid ${C.line}`, background: C.soft, padding: '6px 8px', fontSize: 12,
    fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap',
  },
  td: { border: `1px solid ${C.line}`, padding: '5px 8px', fontSize: 12, verticalAlign: 'top' },
  num: {
    border: `1px solid ${C.line}`, padding: '5px 8px', fontSize: 12, direction: 'ltr', textAlign: 'right',
    whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
  },
  total: { background: C.soft, fontWeight: 800, borderTop: `2px solid ${C.ink}` },
  section: { fontSize: 14, fontWeight: 800, margin: '14px 0 6px' },
  colors: C,
};

/** بطاقات ملخص للطباعة: items = [[label, value], ...] */
export function PrintSummary({ items }) {
  return (
    <div style={{ display: 'flex', border: `1px solid ${C.ink}`, borderRadius: 8, overflow: 'hidden', margin: '8px 0 12px' }}>
      {items.map(([k, v], i) => (
        <div key={k} style={{ flex: 1, padding: '6px 8px', textAlign: 'center', borderRight: i ? `1px solid ${C.ink}` : 'none' }}>
          <div style={{ fontSize: 10.5, color: C.muted }}>{k}</div>
          <div style={{ fontSize: 14, fontWeight: 800, direction: 'ltr' }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export const PrintSheet = forwardRef(function PrintSheet(
  { title, subtitle, period, company, landscape = false, children }, ref,
) {
  return (
    <div ref={ref} dir="rtl" className="rk-print" style={{
      direction: 'rtl', background: '#fff', color: C.ink, padding: '6mm 8mm',
      fontFamily: 'Tajawal, Cairo, "Segoe UI", Tahoma, sans-serif', boxSizing: 'border-box',
    }}>
      <style>{`
        @media print {
          @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .rk-print thead { display: table-header-group; }
          .rk-print tfoot { display: table-row-group; }
          .rk-print tr { page-break-inside: avoid; }
        }
      `}</style>
      <ReportPrintHeader title={title} subTitle={subtitle} dateRange={period} company={company} />
      {children}
      <div style={{
        marginTop: 18, paddingTop: 6, borderTop: `1px solid ${C.line}`,
        display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: C.muted,
      }}>
        <span>طُبع من نظام أرباح | Profit ERP</span>
        <span style={{ direction: 'ltr' }}>{dayjs().format('DD/MM/YYYY HH:mm')}</span>
      </div>
    </div>
  );
});

/** طباعة عنصر عبر react-to-print (v3: contentRef) */
export function useReportPrint(ref, title) {
  return useReactToPrint({ contentRef: ref, documentTitle: `${title}-${dayjs().format('YYYY-MM-DD')}` });
}

/** بيانات الشركة لترويسة الطباعة (تُجلب مرة واحدة وتُحفظ في الذاكرة) */
let companyCache = { id: null, data: null };
export function useCompanyInfo() {
  const id = parseInt(localStorage.getItem('companyId'), 10) || null;
  const [info, setInfo] = useState(companyCache.id === id ? companyCache.data : null);
  useEffect(() => {
    if (!id || (companyCache.id === id && companyCache.data)) return;
    let alive = true;
    axios.get(`/CompanyProfile/public/${id}`)
      .then(r => { companyCache = { id, data: r.data }; if (alive) setInfo(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);
  return info;
}

export const fmtDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '');
